import { useId, useMemo, useState, type ReactNode } from 'react';
import {
  Button,
  Drawer,
  IconButton,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import { Link } from '@backstage/core-components';
import {
  ORGANISATIONS,
  SYSTEMS,
  durationsFor,
  systemById,
  type SystemId,
} from './systems';
import { useTargets } from './useTargets';
import {
  useRoute,
  useSendRequest,
  type RouteState,
  type SendOutcome,
} from './accessApi';

const useStyles = makeStyles(theme => ({
  paper: {
    width: 'min(440px, 100vw)',
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--ui-bg-elevated)',
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 2.5),
    borderBottom: '1px solid var(--ui-border)',
  },
  title: { fontSize: '1.125rem', fontWeight: 600, margin: 0 },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
  },
  pair: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: theme.spacing(1.5),
  },
  routing: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-start',
    padding: theme.spacing(1.5),
    borderRadius: 8,
    border: '1px solid var(--ui-border)',
    background: 'var(--ui-nav-active-bg)',
    color: 'var(--ui-text)',
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },
  routingIcon: { color: 'var(--ui-primary)', marginTop: 2 },
  outcome: {
    padding: theme.spacing(1.5),
    borderRadius: 8,
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },
  failed: {
    border: '1px solid var(--ui-warn-border)',
    background: 'var(--ui-warn-bg)',
    color: 'var(--ui-text)',
  },
  sent: {
    display: 'flex',
    gap: theme.spacing(1),
    border: '1px solid var(--ui-border)',
    background: 'var(--ui-nav-active-bg)',
  },
  foot: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    padding: theme.spacing(2, 2.5),
    borderTop: '1px solid var(--ui-border)',
  },
}));

function RoutingNote({
  state,
  typical,
  targetLabel,
}: {
  state: RouteState;
  typical: string;
  targetLabel: string;
}) {
  let says: ReactNode;
  if (state.status === 'idle') {
    says = `Choose the ${targetLabel.toLowerCase()} to see who answers this.`;
  } else if (state.status === 'loading') {
    says = 'Working out who answers this…';
  } else if (state.status === 'error') {
    says =
      "Couldn't work out who answers this yet. Sending works it out again.";
  } else {
    const { route } = state;
    const who = <strong>{route.approver.title}</strong>;
    let goesTo: ReactNode;
    if (route.reason === 'owner') {
      goesTo = <>Goes to {who}, the owner in the catalog.</>;
    } else if (route.reason === 'no-owner') {
      goesTo = (
        <>
          {route.target.title} has no owner in the catalog, so this goes to{' '}
          {who}, who look after the catalog's data.
        </>
      );
    } else {
      goesTo = (
        <>
          Goes to {who} for now: who answers {route.undecided} is not decided
          yet.
        </>
      );
    }
    says = (
      <>
        {goesTo} Creates a Linear issue in {route.linearTeam.name} and an audit
        entry. Typical answer: {typical}.
      </>
    );
  }
  return <span>{says}</span>;
}

type Props = {
  /** The system the card that opened the panel stands for. */
  systemId: SystemId;
  onClose: () => void;
};

/**
 * The request panel (DX-342; promised in specs/access-request.md): what, on
 * which target, in what scope, for how long and why — and, before sending,
 * who answers it, as the backend works it out from the catalog.
 *
 * Opened afresh by each card, so what it holds is always that card's request.
 * A request that could not be sent keeps everything entered (AC-9).
 */
export const RequestPanel = ({ systemId: initial, onClose }: Props) => {
  const classes = useStyles();
  const titleId = useId();
  // Each field needs an id of its own: MUI ties a TextField's label to its
  // input only through one, and without it the field has no accessible name.
  const fieldId = useId();
  const [systemId, setSystemId] = useState<SystemId>(initial);
  const system = systemById(systemId);
  const targets = useTargets(system.targets);

  const [target, setTarget] = useState('');
  const [repoName, setRepoName] = useState('');
  const [repoOrg, setRepoOrg] = useState(ORGANISATIONS[0]);
  const [scopeLabel, setScopeLabel] = useState(system.scopes[0]?.label ?? '');
  const [scopeText, setScopeText] = useState('');
  const scope = system.scopes.find(s => s.label === scopeLabel);
  const durations = durationsFor(system, scope);
  const [duration, setDuration] = useState(durations?.[0] ?? '');
  const [why, setWhy] = useState('');
  const [problems, setProblems] = useState<{ target?: string; why?: string }>(
    {},
  );
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<SendOutcome | undefined>();

  const naming = Boolean(scope?.newRepository);
  const sentTarget = naming
    ? repoName.trim() && `${repoOrg}/${repoName.trim()}`
    : target;
  const route = useRoute(systemId, sentTarget);
  const send = useSendRequest();

  const shownDuration =
    durations && durations.includes(duration) ? duration : durations?.[0];

  const chooseSystem = (next: SystemId) => {
    const nextSystem = systemById(next);
    setSystemId(next);
    setTarget('');
    setScopeLabel(nextSystem.scopes[0]?.label ?? '');
    setScopeText('');
    setDuration('');
    setProblems({});
    setOutcome(undefined);
  };

  const hint = useMemo(() => {
    const option = targets.options.find(o => o.value === target);
    if (systemId === 'secrets' && option?.slug) {
      // One Infisical project per repository, named like it (DX-16).
      return `Infisical project: ${option.slug.replace(/^.*\//, '')}`;
    }
    return option?.hint;
  }, [targets.options, target, systemId]);

  const onSend = async () => {
    const found: typeof problems = {};
    if (!sentTarget) {
      found.target = naming
        ? 'Name the repository you need.'
        : `Choose the ${system.targetLabel.toLowerCase()} first.`;
    }
    if (!why.trim()) {
      found.why =
        'Say why, in one line the approver can act on: a ticket, an incident or a task.';
    }
    setProblems(found);
    if (found.target || found.why) return;

    setSending(true);
    setOutcome(undefined);
    const result = await send({
      system: systemId,
      target: sentTarget,
      scope: system.scopeText ? scopeText.trim() || undefined : scopeLabel,
      duration: shownDuration,
      why: why.trim(),
    });
    setSending(false);
    setOutcome(result);
  };

  const sent = outcome?.status === 'sent' ? outcome : undefined;

  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      PaperProps={{
        className: classes.paper,
        role: 'dialog',
        'aria-labelledby': titleId,
      }}
    >
      <div className={classes.head}>
        <h2 id={titleId} className={classes.title}>
          Request access
        </h2>
        <IconButton aria-label="Close" size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <div className={classes.body}>
        {sent ? (
          <div role="status" className={`${classes.outcome} ${classes.sent}`}>
            <CheckIcon fontSize="small" className={classes.routingIcon} />
            <span>
              Request sent.{' '}
              <Link to={sent.issue.url}>{sent.issue.identifier}</Link> is open
              in {sent.route.linearTeam.name}, for {sent.route.approver.title}{' '}
              to answer.
            </span>
          </div>
        ) : (
          <>
            <TextField
              select
              id={`${fieldId}-system`}
              label="System"
              value={systemId}
              onChange={e => chooseSystem(e.target.value as SystemId)}
              SelectProps={{ native: true }}
              variant="outlined"
              size="small"
              fullWidth
            >
              {SYSTEMS.map(s => (
                <option key={s.id} value={s.id}>
                  {s.title}
                </option>
              ))}
            </TextField>

            {naming ? (
              <div className={classes.pair}>
                <TextField
                  select
                  id={`${fieldId}-org`}
                  label="Organisation"
                  value={repoOrg}
                  onChange={e => setRepoOrg(e.target.value)}
                  SelectProps={{ native: true }}
                  variant="outlined"
                  size="small"
                >
                  {ORGANISATIONS.map(org => (
                    <option key={org} value={org}>
                      {org}
                    </option>
                  ))}
                </TextField>
                <TextField
                  id={`${fieldId}-repo`}
                  InputLabelProps={{ shrink: true }}
                  label="New repository"
                  value={repoName}
                  onChange={e => setRepoName(e.target.value)}
                  placeholder="new-service"
                  variant="outlined"
                  size="small"
                  error={Boolean(problems.target)}
                  helperText={problems.target}
                />
              </div>
            ) : (
              <TextField
                select
                id={`${fieldId}-target`}
                // Pinned above the field, as on the empty text fields below:
                // left to float, MUI lays the label over "Choose…".
                InputLabelProps={{ shrink: true }}
                label="Target"
                value={target}
                onChange={e => setTarget(e.target.value)}
                SelectProps={{ native: true }}
                variant="outlined"
                size="small"
                fullWidth
                disabled={targets.loading}
                error={Boolean(problems.target) || targets.error}
                helperText={
                  problems.target ??
                  (targets.error
                    ? "Couldn't load what the catalog holds for this."
                    : hint)
                }
              >
                <option value="">
                  {targets.loading ? 'Loading…' : 'Choose…'}
                </option>
                {targets.options.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </TextField>
            )}

            {system.scopes.length > 0 && (
              <TextField
                select
                id={`${fieldId}-scope`}
                label="Scope"
                value={scopeLabel}
                onChange={e => setScopeLabel(e.target.value)}
                SelectProps={{ native: true }}
                variant="outlined"
                size="small"
                fullWidth
              >
                {system.scopes.map(s => (
                  <option key={s.label} value={s.label}>
                    {s.label}
                  </option>
                ))}
              </TextField>
            )}
            {system.scopeText && (
              <TextField
                id={`${fieldId}-scope-text`}
                InputLabelProps={{ shrink: true }}
                label={system.scopeText.label}
                value={scopeText}
                onChange={e => setScopeText(e.target.value)}
                placeholder={system.scopeText.placeholder}
                variant="outlined"
                size="small"
                fullWidth
              />
            )}

            {durations && (
              <TextField
                select
                id={`${fieldId}-duration`}
                label="Duration"
                value={shownDuration}
                onChange={e => setDuration(e.target.value)}
                SelectProps={{ native: true }}
                variant="outlined"
                size="small"
                fullWidth
              >
                {durations.map(d => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </TextField>
            )}

            <TextField
              id={`${fieldId}-why`}
              InputLabelProps={{ shrink: true }}
              label="Why"
              value={why}
              onChange={e => setWhy(e.target.value)}
              placeholder="One line the approver can act on — ticket, incident or task"
              variant="outlined"
              size="small"
              fullWidth
              multiline
              minRows={3}
              error={Boolean(problems.why)}
              helperText={problems.why}
            />

            <div
              role="status"
              aria-label="Who answers"
              className={classes.routing}
            >
              <InfoIcon fontSize="small" className={classes.routingIcon} />
              <RoutingNote
                state={route}
                typical={system.typical}
                targetLabel={naming ? 'repository' : system.targetLabel}
              />
            </div>

            {outcome?.status === 'not-configured' && (
              <Typography
                role="alert"
                className={`${classes.outcome} ${classes.failed}`}
              >
                Requests cannot be sent from the portal yet: its Linear
                integration is not set up. Nothing was sent.
              </Typography>
            )}
            {outcome?.status === 'rate-limited' && (
              <Typography
                role="alert"
                className={`${classes.outcome} ${classes.failed}`}
              >
                The request was not sent: you have sent several in the last
                minute. Everything you entered is still here — send it again in
                a moment.
              </Typography>
            )}
            {outcome?.status === 'failed' && (
              <Typography
                role="alert"
                className={`${classes.outcome} ${classes.failed}`}
              >
                {/* Every failure but "not set up" lands here — a backend that
                    has not started, a refusal, Linear itself — so it names none
                    of them in particular. */}
                The request was not sent: the portal could not file it just now.
                Everything you entered is still here, so you can send it again.
              </Typography>
            )}
          </>
        )}
      </div>

      <div className={classes.foot}>
        {sent ? (
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={onSend}
              disabled={sending}
              startIcon={<CheckIcon />}
            >
              {sending ? 'Sending…' : 'Send request'}
            </Button>
          </>
        )}
      </div>
    </Drawer>
  );
};
